import { Component, ChangeDetectionStrategy, Input, ChangeDetectorRef, HostBinding } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { navigationButtons } from 'layout/navigation-buttons';
import { groupBy } from 'lodash';
import { AccessCondition, AccessString, AuthService, PermissionsHelper } from 'providers/auth.service';
import { EnvironmentService } from 'providers/environment.service';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { FeatureFlagService } from '../../services/feature-flags';
import { BetaLinkService } from '../layout-service/beta-link.service';
import { BetaNotificationDialog } from '../beta-notification-dialog/beta-notification-dialog.component';

interface NavigationButtonSection {
    sectionName: string;
    buttons: typeof navigationButtons;
    active: boolean;
}

@Component({
    selector: 'ce-sidenav',
    templateUrl: './sidenav.component.html',
    styleUrls: ['./sidenav.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class SidenavComponent {
    @Input() mode: 'hidden' | 'collapsed' | 'expanded' = 'collapsed';
    @HostBinding('class') public themeClass: 'theme-mykempower' | 'theme-chargeye' = 'theme-chargeye';

    public navigationButtonSections: NavigationButtonSection[] = [];
    private featureFlagState: { [key: string]: boolean } = {};

    private permissionsHelper: PermissionsHelper;

    skipDialog = false;
    betaLink!: string;

    constructor(
        private auth: AuthService,
        private cdRef: ChangeDetectorRef,
        private featureFlagService: FeatureFlagService,
        private router: Router,
        private environmentService: EnvironmentService,
        private dialog: MatDialog,
        private betaLinkService: BetaLinkService
    ) {
        this.skipDialog = this.betaLinkService.getSkipDialogPreference();
        this.betaLink = this.betaLinkService.betaLink;
        this.initButtons();

        this.environmentService
            .getAppOrigin$()
            .pipe(untilDestroyed(this))
            .subscribe((origin) => {
                this.themeClass = `theme-${origin}`;
                this.cdRef.markForCheck();
            });

        this.router.events
            .pipe(
                filter((ev) => ev instanceof NavigationEnd),
                untilDestroyed(this)
            )
            .subscribe(() => this.refreshButtons());
    }

    private initButtons(): void {
        combineLatest([this.auth.authenticated$, this.featureFlagService.getFlagState$(), this.auth.permissions$])
            .pipe(untilDestroyed(this))
            .subscribe(([, flagState, helper]) => {
                this.featureFlagState = flagState;

                this.permissionsHelper = helper;
                this.refreshButtons();
            });
    }

    private refreshButtons(): void {
        this.navigationButtonSections = Object.values(groupBy(navigationButtons, (n) => n.section))
            .map((buttons) => {
                const filteredButtons: typeof navigationButtons = [];
                for (const b of buttons) {
                    // check duplicate links
                    const linkExisted = filteredButtons.some((_b) => _b.routerLink === b.routerLink);
                    if (linkExisted) {
                        continue;
                    }

                    // tenant id filter
                    if (
                        b.tenantIds &&
                        this.auth.profile?.tenantId &&
                        !b.tenantIds.includes(this.auth.profile.tenantId)
                    ) {
                        // don't add this button, for a different tenant id
                        continue;
                    }

                    // check access
                    if (b.access) {
                        const condition: AccessCondition = b.accessCondition || 'AND';
                        const accessString: AccessString = b.access;

                        if (!this.permissionsHelper.hasAccess(accessString, condition)) {
                            continue;
                        }
                    }

                    // feature flag filter
                    if (b.featureFlag && !this.featureFlagState[b.featureFlag]) {
                        continue;
                    }

                    // sys admin filter - override access filter
                    if (b.sysAdminOnly && !this.auth.getPermissions().isSystemAdmin) {
                        continue;
                    }

                    filteredButtons.push(b);
                }

                return filteredButtons?.length > 0
                    ? ({
                          buttons: filteredButtons,
                          sectionName: buttons[0].section,
                          active: buttons.some((b) => this.router.isActive(b.routerLink, false)),
                      } as NavigationButtonSection)
                    : undefined;
            })
            .filter(Boolean);
        this.cdRef.markForCheck();
    }

    navigateToBeta(redirect?: boolean | undefined): void {
        this.betaLinkService.navigateToBeta(redirect, BetaNotificationDialog);
    }

    openDialog(): void {
        this.betaLinkService.openDialog(BetaNotificationDialog);
    }
}
